import React, { JSX, useCallback, useEffect, useState } from 'react';
import PromotionCode, {
  PromotionCodeData,
  PromotionCodeType,
  validatePromotionCode
} from '../../Components/PromotionCode';
import PackagesList, { ProductIBEProps } from './PackagesList';
import fallback from '../../Translations/generated/product-ibe.de.json';
import Keys from '../../Translations/generated/product-ibe.de.json.keys';
import { useApi, useTranslation } from '@ibe/components';
import { LoggerFactory } from '@ibe/services';
import useSearchApi from '../../Util/useSearchApi';
import {
  BUS_SESSION_STORAGE_KEYS,
  packageFilterSessionStorage,
  validPromotionCodeStorage
} from '../Util/utils';
import dayjs from 'dayjs';
import { productIBEAnchor } from './usePackagesListFunctions';
import {
  ApiBaseData,
  ApiGetPackageDefinitionByCodesRequest,
  ApiPackageModelDefinition
} from '@ibe/api';
import { useAffiliateContext } from '../../Util/AffiliateProvider';
import useThgConfig from '../../Hooks/useThgConfig';
import { GlobalFilterParams } from './PBusFilters';

const logger = LoggerFactory.get('ProductIBE');

type WindowWithCMSFunction = Window & {
  productDetailsUpdatePromoCode: (promoDiscount: number) => void;
};
const ZIP_QUERY_PARAM = 'zip';

declare const window: WindowWithCMSFunction;
export const PackageTypes = {
  PFLUG: 'PFLUG',
  PBUS: 'PBUS'
};

const ProductIBE = (
  props: ProductIBEProps & {
    initialPromotionCode?: string;
    handlePackageSelection?: () => void;
    hidePromotionCode?: boolean;
    isWhitelabel?: boolean;
    basePrice?: string;
    seasonBestPriceDate?: string;
    seasonBestPricePackageCode?: string;
  }
): JSX.Element => {
  const { t } = useTranslation('product-ibe', fallback);
  const searchApi = useSearchApi();
  const {
    earliestPossibleDate,
    latestPossibleDate,
    packageCodes,
    checkoutUrl,
    maintenanceMode,
    handlePackageSelection,
    hidePromotionCode,
    agencyNumber,
    isWhitelabel,
    basePrice,
    seasonBestPriceDate,
    seasonBestPricePackageCode,
    suppressStrikePrices
  } = props;
  let { initialPromotionCode } = props;
  const formattedEarliestPossibleDate = !earliestPossibleDate
    ? ''
    : dayjs(earliestPossibleDate).format('YYYY-MM-DD');
  const formattedLatestPossibleDate = !latestPossibleDate
    ? ''
    : dayjs(latestPossibleDate).add(2, 'week').format('YYYY-MM-DD');

  const hidePromotionCodeWhiteLabel = useAffiliateContext().metaContent?.content?.hidePromotionCode;

  const thgConfig = useThgConfig();

  const [promotionCode, setPromotionCode] = useState<PromotionCodeData>({
    promotionCodeData: undefined,
    initialPromotionCodeChecked: false
  });

  const getDevZipFromQuery = (): string | undefined => {
    if (!thgConfig.enableZipTestingProp) {
      return undefined;
    }

    if (window.location.search === undefined) {
      return undefined;
    }

    const zip = new URLSearchParams(window.location.search).get(ZIP_QUERY_PARAM)?.trim();

    // for now germany only
    return zip && /^\d{5}$/.test(zip) ? zip : undefined;
  };

  const persistGeolocationZipCode = (zipCode: string): void => {
    const zipCodeData: ApiBaseData = {
      code: zipCode,
      description: ''
    };

    sessionStorage.removeItem(BUS_SESSION_STORAGE_KEYS.selectedStations);

    sessionStorage.setItem(BUS_SESSION_STORAGE_KEYS.selectedPostCode, JSON.stringify(zipCodeData));

    sessionStorage.setItem(BUS_SESSION_STORAGE_KEYS.selectedPostCodeFromGeolocation, 'true');

    sessionStorage.setItem(BUS_SESSION_STORAGE_KEYS.geolocationPostCode, zipCode);

    const currentFilters = packageFilterSessionStorage.get() ?? {};
    packageFilterSessionStorage.set({
      ...currentFilters,
      selectedPostalData: zipCodeData
    } as GlobalFilterParams);
  };
  useEffect(() => {
    (async () => {
      // whitelabel should keep the session storage because it is not using cookies
      let startDate: string | undefined = earliestPossibleDate;
      let endDate: string | undefined = latestPossibleDate;
      if (!isWhitelabel) {
        validPromotionCodeStorage.clear();
        startDate = seasonBestPriceDate;
        endDate = seasonBestPriceDate;
      }

      const sessionStorageCode = validPromotionCodeStorage.get();

      if (sessionStorageCode) {
        initialPromotionCode = sessionStorageCode.code;
      }

      if (!!initialPromotionCode || basePrice) {
        try {
          const data = await validatePromotionCode(
            searchApi,
            packageCodes.map(item => item.code),
            initialPromotionCode,
            basePrice,
            startDate,
            endDate
          );
          if (data.code) {
            setPromotionCode({ promotionCodeData: data, initialPromotionCodeChecked: true });
            validPromotionCodeStorage.set(data);
            if (!isWhitelabel) {
              const seasonData = await validatePromotionCode(
                searchApi,
                seasonBestPricePackageCode ? Array.of(seasonBestPricePackageCode) : [],
                initialPromotionCode,
                basePrice,
                seasonBestPriceDate,
                seasonBestPriceDate
              );

              if (
                initialPromotionCode !== seasonData.code &&
                (seasonData.percentageValue || seasonData.absoluteValue)
              ) {
                // call the CMS function to update the right column season card price by the given promoDiscount
                if (window?.productDetailsUpdatePromoCode) {
                  window.productDetailsUpdatePromoCode(
                    seasonData.percentageValue
                      ? seasonData.percentageValue / 100
                      : (seasonData.absoluteValue as number)
                  );
                }
              }
            }
          } else {
            setPromotionCode({ ...promotionCode, initialPromotionCodeChecked: true });
          }
        } catch (err) {
          logger.error(err);
          setPromotionCode({ ...promotionCode, initialPromotionCodeChecked: true });
        }
      } else {
        setPromotionCode({ ...promotionCode, initialPromotionCodeChecked: true });
      }
    })();
  }, [initialPromotionCode]);

  const handlePromotionCode = (code: PromotionCodeType): void => {
    setPromotionCode({ promotionCodeData: code, initialPromotionCodeChecked: true });
  };

  const [packageType, setPackageType] = useState<string | undefined>('');
  const [
    initialPackageTypeRequestFinished,
    setInitialPackageTypeRequestFinished
  ] = useState<boolean>(false);
  const api = useApi();

  const rearmGeolocationAutoSearch = (): void => {
    const geolocationZip = sessionStorage.getItem(BUS_SESSION_STORAGE_KEYS.geolocationPostCode);
    const storedPostCode = sessionStorage.getItem(BUS_SESSION_STORAGE_KEYS.selectedPostCode);

    if (!geolocationZip || !storedPostCode) {
      return;
    }

    try {
      const parsed = JSON.parse(storedPostCode) as ApiBaseData;
      if (parsed?.code === geolocationZip) {
        sessionStorage.setItem(BUS_SESSION_STORAGE_KEYS.selectedPostCodeFromGeolocation, 'true');
      }
    } catch (err) {
      logger.error('Could not parse stored post code', err);
    }
  };

  const getPackages = useCallback(async (): Promise<void> => {
    if (!packageCodes || (!!packageCodes && packageCodes.length === 0)) {
      return;
    }
    let hasPackages = false;
    try {
      const response = await api.getPackagesDefinitionsByCodes({
        packageCodes: [packageCodes[0].code]
      } as ApiGetPackageDefinitionByCodesRequest);

      if (response && packageCodes && packageCodes.length > 0) {
        const packageDefinition = response.find(
          (singlePackage: ApiPackageModelDefinition) => singlePackage.code === packageCodes[0].code
        );

        if (packageDefinition?.typeCode) {
          sessionStorage.setItem('packageType', packageDefinition.typeCode);
          if (packageDefinition.typeCode === PackageTypes.PBUS && thgConfig.enableIpGeolocation) {
            const devZip = getDevZipFromQuery();

            if (devZip) {
              sessionStorage.setItem(BUS_SESSION_STORAGE_KEYS.ipGeolocationAttempted, 'true');
              persistGeolocationZipCode(devZip);
            } else {
              const hasAttempted = sessionStorage.getItem(
                BUS_SESSION_STORAGE_KEYS.ipGeolocationAttempted
              );

              if (!hasAttempted) {
                try {
                  const geoRes = await api.getIpGeolocation();
                  sessionStorage.setItem(BUS_SESSION_STORAGE_KEYS.ipGeolocationAttempted, 'true');
                  sessionStorage.removeItem(
                    BUS_SESSION_STORAGE_KEYS.selectedPostCodeFromGeolocation
                  );

                  if (geoRes.isValidForSearch && geoRes.zipCode) {
                    persistGeolocationZipCode(geoRes.zipCode);
                  }
                } catch (err) {
                  sessionStorage.removeItem(
                    BUS_SESSION_STORAGE_KEYS.selectedPostCodeFromGeolocation
                  );
                  logger.error('Geolocation lookup failed', err);
                }
              } else {
                rearmGeolocationAutoSearch();
              }
            }
          }
          setPackageType(packageDefinition.typeCode);
        }
        hasPackages = true;
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (hasPackages) {
        setTimeout(() => {
          const { hash } = location;
          if (hash) {
            const anchor = document.getElementById(hash.substring(1));
            if (anchor) {
              anchor.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }, 300);
      }
    }
  }, [api, packageCodes, thgConfig.enableIpGeolocation]);

  useEffect(() => {
    (async () => {
      if (!!packageCodes && !initialPackageTypeRequestFinished) {
        await getPackages();
      }
      setInitialPackageTypeRequestFinished(true);
    })();
  }, [packageCodes]);

  return (
    <section aria-labelledby={productIBEAnchor} className="product-ibe">
      {!(hidePromotionCodeWhiteLabel ?? hidePromotionCode) && (
        <PromotionCode
          onSubmit={handlePromotionCode}
          promotionCode={promotionCode.promotionCodeData}
          packageCodes={packageCodes}
          disabled={maintenanceMode}
          startDate={earliestPossibleDate}
          endDate={latestPossibleDate}
          seasonPackageCode={seasonBestPricePackageCode}
          seasonBestPriceDate={seasonBestPriceDate}
          skipCodeComparison
        />
      )}
      <section id={productIBEAnchor + '-div'}>
        <h2 id={productIBEAnchor}>{t(Keys.headline)}</h2>
        <p className="mb-6">{t(Keys.sectionText)}</p>
        {initialPackageTypeRequestFinished && (
          <PackagesList
            packageType={packageType}
            packageCodes={packageCodes}
            earliestPossibleDate={formattedEarliestPossibleDate}
            latestPossibleDate={formattedLatestPossibleDate}
            promotionCode={promotionCode}
            checkoutUrl={checkoutUrl}
            maintenanceMode={maintenanceMode}
            handlePackageSelection={handlePackageSelection}
            agencyNumber={agencyNumber}
            isWhitelabel={isWhitelabel}
            thgConfig={thgConfig}
            suppressStrikePrices={suppressStrikePrices}
          />
        )}
      </section>
    </section>
  );
};

export default ProductIBE;
